import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Box, IconButton, Skeleton, Stack } from '@mui/material'
import {
    RosterProfileQuery,
    RosterProfileQueryVariables,
} from '__generated__/graphql'
import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import {
    CandidateSqlSearchProvider,
    QueryParamsProvider,
} from 'recruiter/contexts'
import { recruiterRouteConfig } from 'recruiter/routes'
import MappingHeader from 'shared/components/MappingHeader'
import { ApolloQueryRefetchFunction } from 'shared/types/graphql'
import { formatAuditDate, getPxAsRem } from 'shared/utils'
import ActionsMenu from '../Search/ActionsMenu'
import { ActionButtonsContainer } from '../styled'
import { RosterProfileMappingCandidateView } from './RosterProfileMappingCandidateView'

export interface RosterMappingProfileProps {
    roster: RosterProfileQuery['roster']
    refetch: ApolloQueryRefetchFunction<
        RosterProfileQuery,
        RosterProfileQueryVariables
    >
    loading?: boolean
}

const RosterMappingProfile: React.FC<RosterMappingProfileProps> = ({
    roster,
    loading = false,
    refetch,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    if (!roster) {
        return loading ? (
            <Stack
                sx={{
                    padding: getPxAsRem(16, 24),
                    gap: getPxAsRem(16),
                }}
            >
                <Skeleton variant="rounded" height="40vh" />
                <Skeleton variant="rounded" height="50vh" />
            </Stack>
        ) : (
            <Navigate to={recruiterRouteConfig.rosters.path} />
        )
    }

    const openMenu = Boolean(anchorEl)

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const canEdit = roster?.permissions?.canEdit

    const actionButtons = canEdit && (
        <>
            <ActionButtonsContainer>
                <IconButton onClick={handleClick}>
                    <MoreVertIcon color="primary" />
                </IconButton>
            </ActionButtonsContainer>
            <ActionsMenu
                open={openMenu}
                anchorEl={anchorEl}
                data={roster}
                handleClose={handleClose}
                refetch={refetch}
            />
        </>
    )

    const verticalSpacingInPixels = 16
    return (
        <Stack
            direction={'column'}
            gap={getPxAsRem(verticalSpacingInPixels)}
            padding={getPxAsRem(verticalSpacingInPixels, 24)}
            height="100%"
        >
            <MappingHeader
                rosterName={roster.rosterName}
                subtitle={
                    roster.company?.value ??
                    formatAuditDate(
                        roster.updatedByUser ? 'Updated' : 'Created',
                        roster.updatedByUser?.preferredName ??
                            roster.createdByUser?.preferredName ??
                            '',
                        roster.updatedAt ?? roster.createdAt
                    )
                }
                showTypePill={true}
                actionButtons={actionButtons}
                canEdit={canEdit}
                showPermissionsIndicator={true}
            />

            <Box overflow="hidden">
                <QueryParamsProvider prefix="roster">
                    <CandidateSqlSearchProvider>
                        <RosterProfileMappingCandidateView
                            roster={roster}
                            refetch={refetch}
                        />
                    </CandidateSqlSearchProvider>
                </QueryParamsProvider>
            </Box>
        </Stack>
    )
}

export default RosterMappingProfile
